import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { MenuItemForm } from "@/components/menu/MenuItemForm";
import { createMenuItem } from "@/services/menu";
import { MenuItemFormData } from "@/types/menu";
import { useIsMobile } from "@/hooks/use-mobile";

interface AddMenuItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onItemCreated?: () => void;
  initialStore?: string;
  initialCategoryId?: string;
}

export function AddMenuItemDialog({
  open,
  onOpenChange,
  onItemCreated,
  initialStore = "",
  initialCategoryId = "",
}: AddMenuItemDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();

  const handleSubmit = async (formData: MenuItemFormData) => {
    setIsSubmitting(true);

    try {
      // We'll need to manually extract the data for the API
      const apiFormData = { ...formData };

      // Pass formData, categoryIds, and tags to the createMenuItem function
      const itemId = await createMenuItem(
        apiFormData,
        formData.categoryIds,
        formData.tags
      );

      toast({
        title: t("addMenuItemDialog.successMessage"),
        description: `${formData.name} ${t(
          "addMenuItemDialog.successDescription"
        )}`,
      });

      if (onItemCreated) {
        onItemCreated();
      }

      onOpenChange(false);
    } catch (error) {
      console.error("Error creating menu item:", error);

      // Show detailed error message to the user
      let errorMessage = t("addMenuItemDialog.errorDescription");
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: t("addMenuItemDialog.errorMessage"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] w-[95vw] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-xl">
            {t("addMenuItemDialog.title")}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t("addMenuItemDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <MenuItemForm
          initialData={{
            name: "",
            description: "",
            price: "",
            available: true,
            store_id: initialStore,
            categoryIds:
              initialCategoryId && initialCategoryId !== "all"
                ? [initialCategoryId]
                : [],
            tags: [],
          }}
          onSubmit={handleSubmit}
          submitButtonText={t("addMenuItemDialog.submitButton")}
          isSubmitting={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  );
}
